/**
 * The ForHim apps and where each one lives (Phase 12e).
 *
 * In a hosted environment every app is a subdomain of one parent domain —
 * `church.forhim.io`, `id.forhim.io`… — so the page's own hostname says which
 * app it is and where the others are; nothing is configured per environment.
 * In development (localhost, a LAN address) there is one origin for
 * everything and no host map: sign-in stays in-app.
 */

export const SITES = ['id', 'church', 'services', 'stream', 'admin'] as const
export type Site = (typeof SITES)[number]

export interface SiteMap {
  /** The app this page is. */
  site: Site
  /** Each app's origin, e.g. `id: 'https://id.forhim.io'`. */
  origins: Record<Site, string>
}

const isSite = (label: string): label is Site => (SITES as readonly string[]).includes(label)

/**
 * The host map for a page at `hostname` (and `protocol`, `https:` by default),
 * or null in development.
 */
export function siteMap(hostname: string, protocol = 'https:'): SiteMap | null {
  const [label, ...rest] = hostname.toLowerCase().split('.')
  if (!label || !isSite(label) || rest.length < 2) return null
  const parent = rest.join('.')
  const origins = Object.fromEntries(
    SITES.map((s) => [s, `${protocol}//${s}.${parent}`]),
  ) as Record<Site, string>
  return { site: label, origins }
}

/**
 * Where it is safe to send someone after signing in: a path on this site, or
 * an address on one of the ForHim apps. Anything else (another domain, a
 * `javascript:` URL, `//evil.example`) falls back to `fallback` — a sign-in
 * page must never be an open redirect.
 */
export function safeRedirect(
  target: string | undefined,
  here: { hostname: string; origin: string; protocol?: string },
  fallback = '/',
): string {
  if (!target) return fallback
  if (target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/\\')) return target
  let url: URL
  try {
    url = new URL(target)
  } catch {
    return fallback
  }
  // This site: a path, which an in-app router can follow.
  if (url.origin === here.origin) return `${url.pathname}${url.search}${url.hash}`
  const map = siteMap(here.hostname, here.protocol)
  if (map && Object.values(map.origins).includes(url.origin)) return url.href
  return fallback
}
